import { useMemo } from 'react';
import { PopoverPosition } from '@mui/material';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { is } from '@client/helpers/dom.guards';

type MenuStoreState = {
  /**
   * Unique identified for any instance that uses this I.E. element.id
   */
  id?: string | number;
  anchorPosition?: PopoverPosition;
};

export const useMenuStore = createWithEqualityFn<MenuStoreState>(
  () => ({}),
  shallow,
);

/**
 * Close menu
 * @returns
 */
export const closeMenu = () => useMenuStore.setState({}, true);

export const openMenu = (state: Required<MenuStoreState>) =>
  useMenuStore.setState({ ...state, id: state.id.toString() });

export const isMenuOpen = (id: MenuStoreState['id']) => {
  const state = useMenuStore.getState();
  return isIdOpen(state, id);
};

const isIdOpen = (state: MenuStoreState, id: MenuStoreState['id']): boolean =>
  !!id && state.id === id.toString() && !!state.anchorPosition;

/**
 * Is menu open or not
 * @param id
 * @returns
 */
export const useIsMenuOpen = (id: MenuStoreState['id']): boolean => {
  return !!useMenuPosition(id);
};

/**
 * Return anchor position for menu if open
 * @param id
 * @returns
 */
export const useMenuPosition = (
  id: MenuStoreState['id'],
): PopoverPosition | undefined => {
  const state = useMenuStore();
  if (isIdOpen(state, id)) {
    return state.anchorPosition;
  }
};

export const useTriggerContextMenu = (
  id: MenuStoreState['id'],
): React.MouseEventHandler<HTMLElement> =>
  useMemo(() => {
    if (!id) {
      return () => {};
    }
    return (e: React.MouseEvent<HTMLElement>) => {
      const anchorPosition = menuMouseHandler(e);
      if (anchorPosition && !isMenuOpen(id)) {
        openMenu({ anchorPosition, id });
      }
    };
  }, [id]);

export const menuMouseHandler = (
  e: React.MouseEvent<Element, globalThis.MouseEvent>,
): PopoverPosition | undefined => {
  if (!e) return;

  e.stopPropagation();
  e.preventDefault();

  const { clientX, clientY, detail, currentTarget } = e;
  const hasPointerPosition = clientX !== 0 || clientY !== 0 || detail !== 0;

  if (hasPointerPosition) return { top: clientY, left: clientX };

  if (is.HTMLElement(currentTarget)) {
    const rect = currentTarget.getBoundingClientRect();
    return {
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width,
    };
  }

  return undefined;
};
